import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, RefreshCcw, Send, ShieldCheck, XCircle, Clock, CheckCircle2, Eye } from "lucide-react";
import { toast } from "sonner";
import { friendlyError } from "@/lib/friendlyError";
import OnboardingPacketPreview from "./OnboardingPacketPreview";

interface Props { instructorId: string }


// Required onboarding steps. Independent Contractor Acknowledgement is no
// longer tracked — the Teaching Agreement is the governing document.
const REQUIRED_STATUS_FIELDS = [
  ["contact_status", "Contact information"],
  ["payment_status", "Payment information"],
  ["w9_status", "Form W-9"],
  ["teaching_agreement_status", "Teaching agreement"],
  ["background_check_status", "Background check"],
] as const;

export default function InstructorOnboardingPanel({ instructorId }: Props) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [packet, setPacket] = useState<any>(null);
  const [token, setToken] = useState<any>(null);
  const [w9, setW9] = useState<any>(null);
  const [instructorStatus, setInstructorStatus] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    const year = new Date().getFullYear();
    const [{ data: pkt }, { data: ins }] = await Promise.all([
      (supabase as any)
        .from("instructor_onboarding_packets")
        .select("*").eq("instructor_id", instructorId).eq("year", year).maybeSingle(),
      (supabase as any).from("instructors").select("status").eq("id", instructorId).maybeSingle(),
    ]);
    setPacket(pkt ?? null);
    setInstructorStatus(ins?.status ?? null);
    if (pkt) {
      const [{ data: tk }, { data: w }] = await Promise.all([
        (supabase as any).from("instructor_onboarding_tokens")
          .select("*").eq("packet_id", pkt.id).is("revoked_at", null)
          .order("created_at", { ascending: false }).limit(1).maybeSingle(),
        (supabase as any).from("instructor_w9_requests")
          .select("*").eq("packet_id", pkt.id)
          .order("created_at", { ascending: false }).limit(1).maybeSingle(),
      ]);
      setToken(tk ?? null);
      setW9(w ?? null);
    }
    setLoading(false);
  }, [instructorId]);

  useEffect(() => { load(); }, [load]);

  const invoke = async (fn: string, body: any, successMsg: string) => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke(fn, { body });
    setBusy(false);
    const errCode = (data as any)?.error;
    if (errCode === "requires_approval" || errCode === "requires_reapproval") {
      toast.error((data as any).message ?? "This packet needs admin approval first.");
      setPreviewOpen(true);
      await load();
      return null;
    }
    if (error || errCode) {
      friendlyError({ what: "I couldn't finish that onboarding step.", technical: error ?? errCode });
      return null;
    }
    toast.success(successMsg);
    await load();
    return data;
  };

  if (loading) return <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Checking this instructor's onboarding…</div>;

  if (!packet) {
    return (
      <Card>
        <CardHeader><CardTitle>Onboarding</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            This instructor has not been sent an onboarding packet yet. Preview and approve
            the packet before it can be sent.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setPreviewOpen(true)}>
              <Eye className="h-4 w-4 mr-2" /> Preview Onboarding Packet
            </Button>
            <Button disabled title="Approve the packet in the preview to enable sending">
              <Send className="h-4 w-4 mr-2" /> Send Onboarding Packet
            </Button>
          </div>
          <OnboardingPacketPreview
            instructorId={instructorId}
            open={previewOpen}
            onOpenChange={setPreviewOpen}
            onReviewChanged={load}
          />
        </CardContent>
      </Card>
    );
  }


  const isVolunteer = instructorStatus === "volunteer";
  // Volunteers still need contact info, teaching agreement, and background
  // check — only the tax/pay items are exempt.
  const volunteerExemptLabels = new Set([
    "Payment information",
    "Form W-9",
  ]);

  const blockers = REQUIRED_STATUS_FIELDS
    .filter(([col, label]) => {
      if (packet[col] === "complete") return false;
      if (isVolunteer && volunteerExemptLabels.has(label)) return false;
      return true;
    })
    .map(([, label]) => label);

  const stepRow = (
    label: string,
    done: boolean,
    meta?: string,
    actions?: React.ReactNode,
    exempt?: boolean,
  ) => (
    <div className="flex items-center justify-between border rounded px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        {exempt ? (
          <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
        ) : done ? (
          <CheckCircle2 className="h-4 w-4 text-green-600" />
        ) : (
          <Clock className="h-4 w-4 text-amber-500" />
        )}
        <div>
          <div className={exempt ? "font-medium text-muted-foreground" : "font-medium"}>{label}</div>
          {exempt ? (
            <div className="text-xs text-muted-foreground">Volunteer — not required</div>
          ) : (
            meta && <div className="text-xs text-muted-foreground">{meta}</div>
          )}
        </div>
      </div>
      <div className="flex gap-2">{!exempt && actions}</div>
    </div>
  );

  const requestChanges = (step: string) =>
    invoke("admin-onboarding-actions", {
      action: "request_changes", packetId: packet.id, step,
    }, "Change request sent");

  const reviewStatus = String(packet.review_status ?? "draft");
  const approved = reviewStatus === "approved";
  const firstSend = !packet.sent_at;

  return (
    <Card>
      <CardHeader className="flex flex-col items-start gap-3 space-y-0 sm:flex-row sm:justify-between">
        <div>
          <CardTitle>Onboarding</CardTitle>
          <div className="mt-2 flex flex-wrap gap-3 text-xs">
            <div><span className="text-muted-foreground">Teaching readiness:</span> <Badge variant={packet.ready_to_teach ? "default" : "secondary"}>{packet.ready_to_teach ? "Ready to Teach" : String(packet.overall_status).replace(/_/g, " ")}</Badge></div>
            <div><span className="text-muted-foreground">Packet review:</span> <Badge variant={approved ? "default" : "outline"}>{approved ? "Approved" : reviewStatus === "ready_for_review" ? "Ready for Review" : "Draft — Not Approved"}</Badge></div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCcw className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
            <Eye className="h-4 w-4 mr-2" /> Preview Onboarding Packet
          </Button>
          <Button size="sm" disabled={busy || !approved}
            title={approved ? undefined : "Preview and approve this packet before sending"}
            onClick={() => invoke("send-onboarding-packet", { instructorId },
              firstSend ? "Onboarding packet sent" : "Onboarding packet resent")}>
            <Send className="h-4 w-4 mr-2" /> {firstSend ? "Send Packet" : "Resend Packet"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <OnboardingPacketPreview
          instructorId={instructorId}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          onReviewChanged={load}
        />
        {!approved && (
          <Alert>
            <AlertDescription className="text-xs">
              This packet has not been approved yet. Use <strong>Preview Onboarding Packet</strong> to
              review exactly what the instructor will receive, then approve it to enable sending.
            </AlertDescription>
          </Alert>
        )}

        {blockers.length > 0 && !packet.ready_to_teach && (
          <Alert>
            <AlertDescription>
              <div className="font-medium mb-1">Blockers to Ready-to-Teach:</div>
              <ul className="list-disc pl-5 text-xs">{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
            </AlertDescription>
          </Alert>
        )}

        {token && (
          <div className="rounded border p-3 text-xs bg-muted/40 space-y-1">
            <div><span className="font-medium">Secure link status:</span> active</div>
            <div><span className="font-medium">Expires:</span> {new Date(token.expires_at).toLocaleString()}</div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => {
                  const newExpiresAt = new Date(Date.now() + 7 * 86400 * 1000).toISOString();
                  return invoke("admin-onboarding-actions", { action: "extend_token", packetId: packet.id, newExpiresAt }, "Extended 7 days");
                }}>
                Extend 7 days
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => invoke("admin-onboarding-actions", { action: "revoke_token", packetId: packet.id }, "Token revoked")}>
                <XCircle className="h-4 w-4 mr-1" /> Revoke
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {stepRow("Contact Information", packet.contact_status === "complete", undefined,
            packet.contact_status === "complete" &&
              <Button size="sm" variant="outline" disabled={busy} onClick={() => requestChanges("contact")}>Request Changes</Button>)}

          {stepRow("Payment Information", packet.payment_status === "complete", undefined,
            packet.payment_status === "complete" &&
              <Button size="sm" variant="outline" disabled={busy} onClick={() => requestChanges("payment")}>Request Changes</Button>,
            isVolunteer)}

          {stepRow(
            "Form W-9 (Secure Tax Provider)",
            packet.w9_status === "complete",
            w9 ? `Provider: ${w9.provider ?? "—"} · Status: ${String(w9.request_status ?? "not_requested").replace(/_/g, " ")}${w9.tin_last4 ? ` · TIN ****${w9.tin_last4}` : ""}` : "Not requested yet",
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => {
                  const providerRequestId = window.prompt("Provider request ID (optional):") ?? undefined;
                  return invoke("admin-onboarding-actions", {
                    action: "w9_request", packetId: packet.id, provider: "track1099", providerRequestId,
                  }, "W-9 request recorded");
                }}>
                Launch W-9 Request
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => {
                  const last4 = window.prompt("Last 4 of TIN on file:")?.trim() ?? "";
                  const type = window.prompt("TIN type (ssn or ein):")?.trim().toLowerCase() ?? "";
                  if (!/^\d{4}$/.test(last4) || !["ssn", "ein"].includes(type)) { toast.error("Invalid input"); return; }
                  return invoke("admin-onboarding-actions", {
                    action: "w9_mark_complete", packetId: packet.id, tinType: type, tinLast4: last4,
                  }, "W-9 marked complete");
                }}>
                Mark Complete
              </Button>
            </div>,
            isVolunteer,
          )}

          {stepRow("Annual Teaching Agreement", packet.teaching_agreement_status === "complete")}

          {stepRow("Background Check", packet.background_check_status === "complete", undefined,
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => {
                const bgProvider = window.prompt("Background check provider (optional):") ?? undefined;
                const bgReference = window.prompt("Provider reference (optional):") ?? undefined;
                return invoke("admin-onboarding-actions", {
                  action: "mark_bg_check_complete", packetId: packet.id, bgProvider, bgReference,
                }, "Background check recorded");
              }}>
              <ShieldCheck className="h-4 w-4 mr-1" /> Mark Complete
            </Button>)}
        </div>

        <div className="pt-2 border-t flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Overall status: <span className="font-medium">{String(packet.overall_status).replace(/_/g, " ")}</span>
            {packet.submitted_at && <> · Submitted {new Date(packet.submitted_at).toLocaleString()}</>}
          </div>
          {!packet.ready_to_teach && (
            <Button size="sm" disabled={busy}
              onClick={() => {
                const hasBlockers = blockers.length > 0;
                const overrideReason = hasBlockers
                  ? (window.prompt(`Override reason (required — blockers: ${blockers.join(", ")}):`) ?? "")
                  : undefined;
                if (hasBlockers && (!overrideReason || overrideReason.length < 3)) return;
                return invoke("admin-onboarding-actions", {
                  action: "mark_ready_to_teach", packetId: packet.id, overrideReason,
                }, "Marked ready to teach");
              }}>
              Mark Ready to Teach
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
